import { FacebookEventType, TikTokEventType } from '@/enums/events'
import { PaymentMethod, PaymentPlatform } from '@/enums/payments'

type TUTMParams = {
  utm_campaign?: string
  utm_medium?: string
  utm_content?: string
  utm_term?: string
  utm_source?: string
  source?: string
  onboardId?: string
  test_event_code?: string
}

type TFBParams = {
  fbc?: string
  fbp?: string
}

type TGAParams = {
  wbraid?: string
  gbraid?: string
  gclid?: string
  google_customer_id?: string
}
type TWParams = {
  twclid?: string
}

type TTiktokParams = {
  ttclid?: string
}

export type TRegisterUserPayload = {
  session_id: string
  page_set_id: string
  email: string
  utm_params: TUTMParams & TFBParams & TGAParams & TWParams & TTiktokParams
  first_visit_at_diff: number
  name?: string | null | undefined
}

export type TReLoginPayload = Omit<TRegisterUserPayload, 'email'>

const contactAccessStates = ['has_subscription', 'has_partner_account'] as const
export type TContactAccessState = (typeof contactAccessStates)[number]

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isContactAccessState(value: any): value is TContactAccessState {
  return contactAccessStates.includes(value)
}

export type TContactRegisterState = 'verification_code_sent' | TContactAccessState

export type TContactRegisterData = {
  app_country_tier: string
  contact_id: string
  state?: TContactRegisterState
}

export type TContactReLoginData = {
  contact_id: string
  state?: TContactAccessState
  app_country_tier: string
}

export type TContactStateData = {
  contact_id: string
  state?: TContactAccessState
}

export type TVerificationPayload = {
  page_set_id: string
  code: string
}

export type TVerificationData = {
  contact_id: string
  access_state?: TContactAccessState
  app_country_tier: string
}

export type TVerificationResendPayload = {
  page_set_id: string
}

export type TSendVerificationCodePayload = {
  email: string
  page_set_id: string
}

export type TVerificationV2Payload = {
  email: string
  page_set_id: string
  code: string
}

export type TVerificationV2Data = TVerificationData & {
  email: string
}

// paypal start

export type TSubscribePlanPayload = {
  page_set_id: string
  plan_id: string
  customer_name?: string
}

export type TSubscriptionPayPalPaymentIntentData = {
  subscription_id: string
  payment_result_url: string
}

export type TCreatePayPalSubscriptionPayload = {
  session_id: string
  page_set_id: string
  plan_id: string
}

export type TCreatePayPalSubscriptionResponse = {
  subscription_id: string
  intent_id: string
}

type TTestEmailPayload = {
  test_email?: string | undefined | null
}

export type TSubscribePayPalV2Payload = Omit<TRegisterUserPayload, 'email' | 'page_set_id'> & {
  session_id: string
  intent_id: string
} & TTestEmailPayload

export type TSubscriptionPayPalPaymentIntentV2Data = {
  email: string
  contact_id: string
}

// paypal end

type TBaseStripeIntentData = {
  client_secret: string
  payment_result_url: string
}

export type TSubscriptionPaymentIntentData = TBaseStripeIntentData & {
  type: string
  country: string | null
}

export type TSubscribeStripeV2Payload = TRegisterUserPayload &
  TSubscribePlanPayload &
  TTestEmailPayload

export type TSubscriptionPaymentIntentV2Data = TSubscriptionPaymentIntentData & {
  email: string
}

export type TLinkCredentialsData = {
  unlock_link: string
}

export type TPasswordCredentialsData = {
  username: string
  password: string
}

export type TLTPData = {
  id: string
  name: string
}

type TEventDataBase = {
  content_ids?: string[]
  content_name?: string
  value?: number
  currency?: string
}

export type TTwitterEventData = TEventDataBase & {
  twclid?: string
}

export type TTwitterEvent = TTwitterEventData & {
  event_id: string
  pixel_id: string
  conversion_id: string
}

export type TFacebookEventData = TEventDataBase & {
  fbc?: string
  fbp?: string
}

export type TFacebookEvent = TFacebookEventData & {
  event_id: string
  event_name: FacebookEventType
  pixel_id: number
  event_source_url: string
  external_id: string
}

export type TTikTokEventData = TEventDataBase & {
  ttclid?: string
}

export type TTikTokEvent = TTikTokEventData & {
  event_id: string
  external_id: string
  event_name: TikTokEventType
  event_source_url: string
  pixel_id: string
  test_event_code?: string
}

export type TGoogleAdsEventData = TEventDataBase & {
  gclid?: string
  wbraid?: string
  gbraid?: string
}

export type TGoogleAdsEvent = TGoogleAdsEventData & {
  event_id: string
  customer_id: string
  conversion_id: string
  conversion_value?: number
  external_id: string
}

// token in header
export type TInternalAnalyticsEventData = {
  stage_variation: string[]
  stage_values: string[]

  referer_url?: string
  plan_id?: string
  ltp_content_price_id?: string
  ad_source?: string
  ad_campaign_id?: string
  ad_set_id?: string
  ad_id?: string
  ad_placement?: string

  charged_amount_usd?: number
  payment_method?: PaymentMethod
  payment_platform?: PaymentPlatform
  platform?: string
  country?: string
  lang?: string
}

// LTP

type TBasePurchaseContentPayload = {
  page_set_id: string
  internal_content_price_id: string
}

export type TFastPurchaseContentPayload = TBasePurchaseContentPayload & {
  success_page: string
  fail_page: string
}

export type TFastPurchaseContentData = {
  client_secret?: string
}

export type TPurchaseContentPayload = TRegisterUserPayload &
  TTestEmailPayload &
  TBasePurchaseContentPayload

export type TPurchaseContentData = TBaseStripeIntentData & {
  payment_result_url: string
  country: string | null
  email: string
}

export type TCreatePayPalPurchasePayload = {
  session_id: string
  page_set_id: string
  internal_content_price_id: string
}

export type TCreatePayPalPurchaseResponse = {
  order_id: string
  intent_id: string
}

export type TApprovePayPalPurchasePayload = Omit<TRegisterUserPayload, 'email' | 'page_set_id'> & {
  session_id: string
  purchase_intent_id: string
} & TTestEmailPayload

export type TApprovePayPalPurchaseIntentData = {
  email: string
  contact_id: string
}

// analytics

export type TInternalAnalyticsEvent = TInternalAnalyticsEventData & {
  event_id: string
  event: string
  stage_name: string
  partner_id: string // add
  app_id: string
  page_set_id: string
  session_id: string
  url: string
  milliseconds_since_first_visit: number
}

export type TAnalyticsEventType = 'facebook' | 'analytics' | 'twitter' | 'tiktok' | 'google_ads'

export type TAnalyticsEvent = {
  type: TAnalyticsEventType
  data: TFacebookEvent | TInternalAnalyticsEvent | TTwitterEvent | TTikTokEvent | TGoogleAdsEvent
}
